import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Paper, Stack, Switch, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  configurarResumoSemanal,
  gerarResumo,
  listarResumos,
  situacaoResumoSemanal,
  tituloResumo,
  type AgendaResumo,
  type Painel,
  type ResumoSemanal,
  type TipoResumo,
} from '../api/resumos'
import { BotaoExpandir } from '../components/BotaoExpandir'
import { ArvoreDiagnostico } from '../components/DiagnosticoCusto'
import { DestinatariosRelatorio } from '../components/DestinatariosRelatorio'
import { EnviarRelatorioDialog } from '../components/EnviarRelatorioDialog'
import { IndicadoresResumo } from '../components/IndicadoresResumo'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'

const PAINEL: Record<Painel, { nome: string; rota: string }> = {
  trafego_pago: { nome: 'Tráfego pago', rota: '/trafego-pago' },
  organico: { nome: 'Orgânico', rota: '/organico' },
  redes_sociais: { nome: 'Redes sociais', rota: '/redes-sociais' },
}
const br = (iso: string) => iso.split('-').reverse().join('/')
const curta = (iso: string) => br(iso).slice(0, 5)
const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const rotuloChip = (r: ResumoSemanal) => (r.tipo === 'mes' ? `${MES_CURTO[Number(r.inicio.slice(5, 7)) - 1]}/${r.inicio.slice(0, 4)}` : `${curta(r.inicio)} a ${curta(r.fim)}`)
const PASSADO: Record<TipoResumo, string> = { semana: 'Gerar o da semana passada', mes: 'Gerar o do mês passado' }
const quando = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' })

/**
 * Resumos semanais e relatórios mensais de cada marca (os três painéis, as campanhas, os
 * indicadores e o diagnóstico). Com uma marca no filtro, o histórico dela; com todas, o mais
 * recente de cada uma. Daqui sai o PDF e o envio por e-mail.
 */
export function ResumosPage() {
  const { marcaId, marcas, setMarcaId } = useFiltros()
  const { pode } = useAuth()
  const gerencia = pode('marcas.gerenciar')
  const [busca, setBusca] = useSearchParams()
  const tipo: TipoResumo = busca.get('tipo') === 'mes' ? 'mes' : 'semana'
  const [agendaLigada, setAgendaLigada] = useState(false)
  const [enviando, setEnviando] = useState<ResumoSemanal | null>(null)
  const [resumos, setResumos] = useState<ResumoSemanal[] | null>(null)
  const [escolhido, setEscolhido] = useState<string>('')
  const [gerando, setGerando] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  // O link do e-mail traz a marca (?marca=): vira o filtro, e sai da URL.
  const marcaDoLink = busca.get('marca')
  useEffect(() => {
    if (!marcaDoLink || !marcas.some((m) => m.id === marcaDoLink)) return
    setMarcaId(marcaDoLink)
    setBusca((b) => {
      b.delete('marca')
      return b
    }, { replace: true })
  }, [marcaDoLink, marcas, setMarcaId, setBusca])

  const carregar = useCallback(async () => {
    try {
      setResumos(await listarResumos(marcaId || null, tipo))
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [marcaId, tipo])

  useEffect(() => {
    setResumos(null)
    setEscolhido('')
    carregar()
  }, [carregar])

  const gerar = async (marca: string, inicio?: string) => {
    setGerando(marca)
    setErro(null)
    setAviso(null)
    try {
      const r = await gerarResumo(marca, tipo, inicio)
      if (r.motivoSemIa) setAviso(`Resumo automático, sem a IA: ${r.motivoSemIa}.`)
      await carregar()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setGerando(null)
    }
  }

  const marcaNome = marcas.find((m) => m.id === marcaId)?.nome

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="flex-start" spacing={2} useFlexGap flexWrap="wrap">
        <Box sx={{ mr: 'auto', maxWidth: 760 }}>
          <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
            Resumos e relatórios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {tipo === 'mes'
              ? 'O mês civil anterior de cada marca: os indicadores contra o período anterior, a meta, o diagnóstico do custo e o que fazer no próximo mês.'
              : 'A semana anterior de cada marca, de segunda a domingo: tráfego pago, orgânico, redes sociais e o que as campanhas pedem.'}{' '}
            Escrito pela IA configurada, com cada número conferido — ou montado pelo sistema, sem IA.
          </Typography>
        </Box>
        <Agenda onSituacao={setAgendaLigada} />
      </Stack>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={tipo}
        onChange={(_, v: TipoResumo | null) => {
          if (!v) return
          setBusca((b) => {
            if (v === 'mes') b.set('tipo', 'mes')
            else b.delete('tipo')
            return b
          })
        }}
        sx={{ alignSelf: 'flex-start' }}
      >
        <ToggleButton value="semana" sx={{ textTransform: 'none', px: 2 }}>
          Semanal
        </ToggleButton>
        <ToggleButton value="mes" sx={{ textTransform: 'none', px: 2 }}>
          Mensal
        </ToggleButton>
      </ToggleButtonGroup>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}
      {aviso ? (
        <Alert severity="info" onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      ) : null}

      {!resumos ? (
        // Falhou ao carregar: o erro já está acima; sem roda girando para sempre.
        erro ? null : (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )
      ) : marcaId ? (
        <HistoricoDaMarca
          tipo={tipo}
          marca={marcaNome ?? ''}
          resumos={resumos}
          escolhido={escolhido || resumos[0]?.id || ''}
          onEscolher={setEscolhido}
          gerando={gerando === marcaId}
          onGerar={(inicio) => gerar(marcaId, inicio)}
          onEnviar={gerencia ? setEnviando : undefined}
        />
      ) : (
        <Stack spacing={2}>
          {marcas.map((m) => {
            const ultimo = resumos.find((r) => r.marcaId === m.id)
            return ultimo ? (
              <ResumoCard
                key={m.id}
                r={ultimo}
                mostrarMarca
                gerando={gerando === m.id}
                onGerar={() => gerar(m.id, ultimo.inicio)}
                onEnviar={gerencia ? () => setEnviando(ultimo) : undefined}
              />
            ) : (
              <Paper key={m.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 'auto' }}>
                    {m.nome}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sem resumo ainda.
                  </Typography>
                  <Button size="small" variant="outlined" disabled={Boolean(gerando)} onClick={() => gerar(m.id)} sx={{ textTransform: 'none' }}>
                    {gerando === m.id ? 'Gerando…' : PASSADO[tipo]}
                  </Button>
                </Stack>
              </Paper>
            )
          })}
        </Stack>
      )}

      {marcaId && gerencia ? <DestinatariosRelatorio marcaId={marcaId} marca={marcaNome ?? ''} agendaLigada={agendaLigada} /> : null}
      {enviando ? <EnviarRelatorioDialog r={enviando} onClose={() => setEnviando(null)} /> : null}
    </Stack>
  )
}

function HistoricoDaMarca(p: {
  tipo: TipoResumo
  marca: string
  resumos: ResumoSemanal[]
  escolhido: string
  onEscolher: (id: string) => void
  gerando: boolean
  onGerar: (inicio?: string) => void
  onEnviar?: (r: ResumoSemanal) => void
}) {
  const r = p.resumos.find((x) => x.id === p.escolhido)
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
        {p.resumos.map((x) => (
          <Chip
            key={x.id}
            label={rotuloChip(x)}
            color={x.id === p.escolhido ? 'primary' : 'default'}
            variant={x.id === p.escolhido ? 'filled' : 'outlined'}
            onClick={() => p.onEscolher(x.id)}
          />
        ))}
        <Button size="small" variant="contained" disabled={p.gerando} onClick={() => p.onGerar()} sx={{ textTransform: 'none', ml: 'auto' }}>
          {p.gerando ? 'Gerando…' : PASSADO[p.tipo]}
        </Button>
      </Stack>
      {r ? (
        <ResumoCard r={r} gerando={p.gerando} onGerar={() => p.onGerar(r.inicio)} onEnviar={p.onEnviar ? () => p.onEnviar!(r) : undefined} />
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            {p.tipo === 'mes'
              ? `Nenhum relatório mensal de ${p.marca} ainda. Gere o do mês passado — ou ligue o resumo automático (o mensal sai todo dia 1).`
              : `Nenhum resumo de ${p.marca} ainda. Gere o da semana passada — ou ligue o resumo automático das segundas.`}
          </Typography>
        </Paper>
      )}
    </Stack>
  )
}

function Lista({ titulo, itens, cor }: { titulo: string; itens: string[]; cor: string }) {
  if (!itens.length) return null
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" sx={{ color: cor, fontWeight: 700, letterSpacing: 0.6 }}>
        {titulo}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {itens.map((t) => (
          <Typography component="li" variant="body2" key={t} sx={{ mb: 0.75 }}>
            {t}
          </Typography>
        ))}
      </Box>
    </Box>
  )
}

function ResumoCard({
  r,
  mostrarMarca,
  gerando,
  onGerar,
  onEnviar,
}: {
  r: ResumoSemanal
  mostrarMarca?: boolean
  gerando: boolean
  onGerar: () => void
  onEnviar?: () => void
}) {
  const [verAchados, setVerAchados] = useState(false)
  const [verDiagnostico, setVerDiagnostico] = useState(false)
  const c = r.conteudo
  const ia = r.fonte === 'ia'
  return (
    <Paper variant="outlined" sx={{ p: 2.5, opacity: gerando ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif', mr: 'auto' }}>
          {mostrarMarca ? `${r.marca} · ` : ''}
          {tituloResumo(r)}
        </Typography>
        <Chip size="small" variant="outlined" color={ia ? 'primary' : 'default'} label={ia ? 'Escrito pela IA · números conferidos' : 'Resumo automático'} />
        <Button size="small" disabled={gerando} onClick={onGerar} sx={{ textTransform: 'none' }}>
          {gerando ? 'Gerando…' : 'Gerar de novo'}
        </Button>
        <Button size="small" component="a" href={`/relatorio/${r.id}`} target="_blank" rel="noopener" sx={{ textTransform: 'none' }}>
          Exportar PDF
        </Button>
        {onEnviar ? (
          <Button size="small" variant="outlined" onClick={onEnviar} sx={{ textTransform: 'none' }}>
            Enviar por e-mail
          </Button>
        ) : null}
      </Stack>

      {c.demo ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Inclui dados de demonstração desta marca — números fictícios. Remova a demonstração em Configurações → Marcas quando os dados reais entrarem.
        </Alert>
      ) : null}

      <Typography variant="body1" sx={{ lineHeight: 1.65 }}>
        {c.resumo}
      </Typography>

      {c.indicadores?.length ? (
        <Box sx={{ mt: 2 }}>
          <IndicadoresResumo itens={c.indicadores} />
        </Box>
      ) : null}

      <Box sx={{ display: 'grid', gap: 2, mt: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' } }}>
        <Lista titulo="Destaques" itens={c.destaques} cor="success.main" />
        <Lista titulo="Atenção" itens={c.atencao} cor="warning.main" />
        <Lista titulo={r.tipo === 'mes' ? 'Para o próximo mês' : 'Para esta semana'} itens={c.acoes} cor="primary.main" />
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
        {c.paineis.length ? (
          <BotaoExpandir aberto={verAchados} icone="document" onClick={() => setVerAchados((v) => !v)}>
            {verAchados ? 'Esconder os achados' : 'Ver os achados de cada painel'}
          </BotaoExpandir>
        ) : null}
        {c.diagnostico?.length ? (
          <BotaoExpandir aberto={verDiagnostico} icone="graph" onClick={() => setVerDiagnostico((v) => !v)}>
            {verDiagnostico ? 'Esconder o diagnóstico' : 'Ver o diagnóstico do custo'}
          </BotaoExpandir>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {r.origem === 'agendada' ? 'Gerado automaticamente' : 'Gerado à mão'} em {quando.format(new Date(r.geradoEm))}
          {ia && c.descartadas ? ` · ${c.descartadas} ${c.descartadas === 1 ? 'item da IA descartado' : 'itens da IA descartados'} na conferência` : ''}
        </Typography>
      </Stack>

      <Collapse in={verDiagnostico}>
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          <ArvoreDiagnostico arvore={c.diagnostico ?? []} />
        </Stack>
      </Collapse>

      <Collapse in={verAchados}>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {c.paineis.map((p) => (
            <Box key={p.painel}>
              <Typography variant="subtitle2">
                {PAINEL[p.painel].nome}{' '}
                <Button component={RouterLink} to={PAINEL[p.painel].rota} size="small" sx={{ textTransform: 'none', ml: 0.5 }}>
                  abrir o painel
                </Button>
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {p.achados.map((a) => (
                  <Typography component="li" variant="body2" color="text.secondary" key={a.texto}>
                    {a.texto}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  )
}

/** O resumo automático (segundas) e o relatório mensal (dia 1): liga quem gerencia marcas; os outros veem. */
function Agenda({ onSituacao }: { onSituacao: (ligado: boolean) => void }) {
  const { pode } = useAuth()
  const [s, setS] = useState<AgendaResumo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const gerencia = pode('marcas.gerenciar')

  useEffect(() => {
    situacaoResumoSemanal()
      .then(setS)
      .catch((e: Error) => setErro(e.message))
  }, [])
  useEffect(() => onSituacao(Boolean(s?.ativo)), [s, onSituacao])

  if (!s) return erro ? <Typography variant="caption" color="error">{erro}</Typography> : null
  const alternar = async (ativo: boolean) => {
    setSalvando(true)
    setErro(null)
    try {
      setS(await configurarResumoSemanal(ativo))
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setSalvando(false)
    }
  }
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Resumo automático
          </Typography>
          <Typography variant="caption" color={erro ? 'error' : 'text.secondary'}>
            {erro ??
              (!s.disponivel ? 'Agendador do banco indisponível' : s.ativo ? `Ligado: semanal toda ${s.quando}; mensal ${s.quando_mensal ?? 'dia 1'}` : 'Desligado')}
          </Typography>
        </Box>
        {gerencia ? (
          <Switch
            checked={s.ativo}
            disabled={!s.disponivel || salvando}
            onChange={(e) => alternar(e.target.checked)}
            slotProps={{ input: { 'aria-label': 'Resumo automático toda segunda e relatório mensal todo dia 1' } }}
          />
        ) : null}
      </Stack>
    </Paper>
  )
}
